// ─────────────────────────────────────────────────────────────────
// Glass-i — DGU Consumables master page (Stage DGU-CONSUMABLE-1).
//
// Standalone, isolated master (mirrors the Interlayer Items page) for the DGU
// (double-glazed unit) BOM consumables: SPACER (running m — the "12A" air gap),
// DESICCANT (kg), PRIMARY / SECONDARY SEALANT (kg), CORNER KEY (pcs). Each is a
// consumable inventory_items row carrying an item_dgu_consumable_specs extension
// (kind + attributes + pack→stock conversion). It does NOT touch the Item grid,
// Product Studio, purchase, proforma, or any stock/ledger.
//
// Phase 1 is master-data only: define the items + pack conversion. Receiving
// (Phase 2) and manual issue on DGU production (Phase 3) come later.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Table, Button, Space, Input, Select, InputNumber, Modal, Form,
  Tag, Tooltip, Typography, Alert, Popconfirm, message
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, BorderOuterOutlined
} from '@ant-design/icons';

import useAuth from '../../../../hooks/useAuth';
import { itemsApi, studioLookups } from '../items/itemsApi';
import {
  KIND_OPTIONS, PACK_OPTIONS, UOM_OPTIONS, DEFAULT_UOM, DEFAULT_PACK,
  kindLabel, kindColor, fmtNum3
} from './dguConsumableHelpers';
import './DguConsumables.css';

const { Title, Text } = Typography;

const PERM = Object.freeze({
  VIEW:   'erp.masters.items.view',
  CREATE: 'erp.masters.items.create',
  UPDATE: 'erp.masters.items.update'
});
function hasPermission(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;

// Live "≈ X <uom> per <pack>" preview from the form's current pack size.
function PackPreview({ form }) {
  Form.useWatch('packSize', form);
  Form.useWatch('packType', form);
  Form.useWatch('stockUom', form);
  const size = Number(form.getFieldValue('packSize'));
  const pack = form.getFieldValue('packType');
  const uom = form.getFieldValue('stockUom') || 'unit';
  if (!Number.isFinite(size) || size <= 0 || !pack) {
    return <span className="dgu-conv__figure dgu-conv__figure--empty">— per pack</span>;
  }
  return (
    <span className="dgu-conv__figure">
      <b className="tabular-nums">{fmtNum3(size)}</b> {uom} / {pack}
    </span>
  );
}

export default function DguConsumablesPage() {
  const { me } = useAuth();
  const canCreate = hasPermission(me, PERM.CREATE);
  const canUpdate = hasPermission(me, PERM.UPDATE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uomOpts, setUomOpts] = useState([]);   // {value:id, code}
  const [form] = Form.useForm();
  const kind = Form.useWatch('consumableKind', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itemsApi.listDguConsumables({
        page, pageSize,
        search: search || undefined,
        consumableKind: kindFilter || undefined
      });
      setRows((res && res.rows) || []);
      setTotal((res && res.total) || 0);
    } catch (e) {
      message.error(errMsg(e, 'Failed to load DGU consumables.'));
    } finally { setLoading(false); }
  }, [page, pageSize, search, kindFilter]);
  useEffect(() => { load(); }, [load]);

  // UOM master rows → {value:id, code} so we can resolve the chosen stock UOM
  // code to a default_uom_id when creating the item.
  const loadUoms = useCallback(async () => {
    try {
      const list = await studioLookups.uoms({ q: '', limit: 200 });
      const opts = (Array.isArray(list) ? list : []).map((u) => ({
        value: u.id || u.uomId || u.value,
        code:  (u.uomCode || u.uom_code || u.label || '').toUpperCase()
      })).filter((o) => o.value && o.code);
      setUomOpts(opts);
      return opts;
    } catch { return []; }
  }, []);

  const openCreate = useCallback(async () => {
    setEditing(null);
    form.resetFields();
    await loadUoms();
    form.setFieldsValue({ consumableKind: 'spacer', stockUom: DEFAULT_UOM.spacer, packType: DEFAULT_PACK.spacer });
    setModalOpen(true);
  }, [form, loadUoms]);

  const openEdit = useCallback(async (row) => {
    setEditing(row);
    form.resetFields();
    await loadUoms();
    let spec = {};
    try { spec = (await itemsApi.getDguConsumableSpec(row.itemId)) || {}; } catch { /* fall back to list row */ }
    form.setFieldsValue({
      itemName:        row.itemName,
      consumableKind:  spec.consumableKind || row.consumableKind || undefined,
      stockUom:        spec.stockUom || row.stockUom || undefined,
      airGapMm:        spec.airGapMm ?? row.airGapMm ?? null,
      material:        spec.material ?? row.material ?? '',
      color:           spec.color ?? '',
      brand:           spec.brand ?? row.brand ?? '',
      packType:        spec.packType ?? row.packType ?? undefined,
      packSize:        spec.packSize ?? row.packSize ?? null,
      perUnitFactor:   spec.perUnitFactor ?? row.perUnitFactor ?? null,
      shelfLifeMonths: spec.shelfLifeMonths ?? row.shelfLifeMonths ?? null,
      remarks:         spec.remarks ?? ''
    });
    setModalOpen(true);
  }, [form, loadUoms]);

  // On kind change, pre-fill the sensible stock unit + pack type (only when the
  // user hasn't already picked, so an edit isn't clobbered).
  const onKindChange = useCallback((v) => {
    const patch = { consumableKind: v };
    if (!form.getFieldValue('stockUom')) patch.stockUom = DEFAULT_UOM[v];
    if (!form.getFieldValue('packType')) patch.packType = DEFAULT_PACK[v];
    if (v !== 'spacer') patch.airGapMm = null;
    form.setFieldsValue(patch);
  }, [form]);

  const submit = useCallback(async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const spec = {
        consumableKind:  v.consumableKind || null,
        stockUom:        v.stockUom || null,
        airGapMm:        v.consumableKind === 'spacer' ? (v.airGapMm ?? null) : null,
        material:        v.material || null,
        color:           v.color || null,
        brand:           v.brand || null,
        packType:        v.packType || null,
        packSize:        v.packSize ?? null,
        perUnitFactor:   v.perUnitFactor ?? null,
        shelfLifeMonths: v.shelfLifeMonths ?? null,
        remarks:         v.remarks || null
      };
      // Resolve the chosen stock UOM code → uom_master id for default_uom_id.
      const uomRow = uomOpts.find((o) => o.code === String(v.stockUom || '').toUpperCase());
      let itemId;
      if (editing) {
        itemId = editing.itemId;
        await itemsApi.update(itemId, { itemName: v.itemName, ...(uomRow ? { defaultUomId: uomRow.value } : {}) });
      } else {
        const core = {
          itemName:      v.itemName,
          itemType:      'consumable',
          isGoods:       true,
          isService:     false,
          isStockItem:   true,
          isPurchasable: true,
          isSellable:    false,
          isConsumable:  true
        };
        if (uomRow) core.defaultUomId = uomRow.value;
        const item = await itemsApi.create(core);
        itemId = item.id;
      }
      await itemsApi.upsertDguConsumableSpec(itemId, spec);
      message.success(editing ? 'DGU consumable updated.' : 'DGU consumable created.');
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(errMsg(e, 'Save failed.'));
    } finally { setSaving(false); }
  }, [form, editing, uomOpts, load]);

  const removeSpec = useCallback(async (row) => {
    try {
      await itemsApi.deleteDguConsumableSpec(row.itemId);
      message.success(`${row.itemName} is no longer marked as a DGU consumable.`);
      await load();
    } catch (e) { message.error(errMsg(e, 'Could not remove the DGU consumable spec.')); }
  }, [load]);

  const onSearch = useCallback((val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const columns = useMemo(() => ([
    { title: 'Item', dataIndex: 'itemName', key: 'itemName', ellipsis: true,
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <Tooltip title={v}><span style={{ fontWeight: 600 }}>{v}</span></Tooltip>
          <span style={{ fontSize: 10.5, color: '#94a3b8' }}>{r.itemCode}</span>
        </div>
      ) },
    { title: 'Kind', dataIndex: 'consumableKind', key: 'consumableKind', width: 140,
      render: (v) => <Tag color={kindColor(v)} style={{ marginInlineEnd: 0 }}>{kindLabel(v)}</Tag> },
    { title: 'Air gap', dataIndex: 'airGapMm', key: 'airGapMm', width: 84, align: 'right',
      render: (v) => (v != null ? <span className="tabular-nums">{v} mm</span> : <Text type="secondary">—</Text>) },
    { title: 'Material', dataIndex: 'material', key: 'material', width: 130,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Brand', dataIndex: 'brand', key: 'brand', width: 120,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Pack', key: 'pack', width: 130, align: 'right',
      render: (_, r) => (r.packType
        ? <span className="tabular-nums">{r.packType}{r.packSize != null ? ` · ${fmtNum3(r.packSize)}` : ''}</span>
        : <Text type="secondary">—</Text>) },
    { title: 'Stock UOM', dataIndex: 'stockUom', key: 'stockUom', width: 96,
      render: (v, r) => v || r.uom || <Text type="secondary">—</Text> },
    { title: 'Actions', key: 'actions', width: 96, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          {canUpdate ? (
            <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          ) : null}
          {canUpdate ? (
            <Popconfirm title="Remove DGU consumable spec?" description="The item stays; it just won't be listed here." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeSpec(r)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : null}
        </Space>
      ) }
  ]), [canUpdate, openEdit, removeSpec]);

  return (
    <div className="p-4 dgu-cons-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <BorderOuterOutlined style={{ marginRight: 8 }} />DGU Consumables
          </Title>
          <Text type="secondary">Spacer (m), desiccant &amp; sealant (kg), corner keys (pcs) — the DGU BOM.</Text>
        </div>
        <Space>
          <Input.Search allowClear placeholder="Search name / code / brand" style={{ width: 240 }} onChange={(e) => onSearch(e.target.value)} />
          <Select allowClear placeholder="All kinds" style={{ width: 160 }} value={kindFilter}
            onChange={(v) => { setKindFilter(v || undefined); setPage(1); }} options={KIND_OPTIONS} />
          <Button icon={<ReloadOutlined />} onClick={load} />
          {canCreate ? <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add consumable</Button> : null}
        </Space>
      </div>

      <Alert type="info" showIcon className="mb-3"
        message="Master data only"
        description="Define each DGU consumable and its pack→stock conversion here. Receive stock through Purchase; consumption on DGU production is a later phase." />

      <Card size="small" className="dgu-cons-grid" bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="itemId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          sticky
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true, size: 'small',
            pageSizeOptions: ['25', '50', '100'],
            onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
            showTotal: (t) => `${t} DGU consumable${t === 1 ? '' : 's'}`
          }}
          locale={{ emptyText: loading ? ' ' : 'No DGU consumables yet — add your first spacer / desiccant / sealant / corner key.' }}
        />
      </Card>

      <Modal
        title={editing ? `Edit DGU consumable · ${editing.itemName}` : 'Add DGU consumable'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        destroyOnClose
        width={560}
        className="dgu-cons-modal"
      >
        <Form form={form} layout="vertical" requiredMark="optional" size="small" className="dgu-form">
          <Form.Item name="itemName" label="Item name" rules={[{ required: true, message: 'Give the consumable a name' }]}>
            <Input maxLength={200} placeholder="e.g. 12mm Aluminium Spacer" />
          </Form.Item>

          <div className="grid grid-cols-12 gap-x-3">
            <Form.Item className="col-span-4" name="consumableKind" label="Kind" rules={[{ required: true, message: 'Required' }]}>
              <Select options={KIND_OPTIONS} onChange={onKindChange} />
            </Form.Item>
            {kind === 'spacer' ? (
              <Form.Item className="col-span-4" name="airGapMm" label="Air gap (mm)" tooltip="The DGU gap — 6 / 8 / 12 / 16 / 20"
                rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="12" />
              </Form.Item>
            ) : (
              <Form.Item className="col-span-4" name="material" label="Material">
                <Input maxLength={60} placeholder="molecular sieve" />
              </Form.Item>
            )}
            <Form.Item className="col-span-4" name="stockUom" label="Stock UOM" tooltip="m = RMT, weight = KGS, count = NOS">
              <Select options={UOM_OPTIONS} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-12 gap-x-3">
            {kind === 'spacer' ? (
              <Form.Item className="col-span-4" name="material" label="Material">
                <Input maxLength={60} placeholder="aluminium / warm edge" />
              </Form.Item>
            ) : null}
            <Form.Item className={kind === 'spacer' ? 'col-span-4' : 'col-span-6'} name="color" label="Colour">
              <Input maxLength={80} placeholder="mill / black" />
            </Form.Item>
            <Form.Item className={kind === 'spacer' ? 'col-span-4' : 'col-span-6'} name="brand" label="Brand">
              <Input maxLength={80} placeholder="brand" />
            </Form.Item>
          </div>

          {/* Signature — pack → stock-unit conversion */}
          <div className="dgu-conv">
            <div className="dgu-conv__head">
              <span className="dgu-conv__title">Pack → stock conversion</span>
              <PackPreview form={form} />
            </div>
            <div className="grid grid-cols-3 gap-x-3">
              <Form.Item name="packType" label="Bought as">
                <Select allowClear options={PACK_OPTIONS} placeholder="BAR" />
              </Form.Item>
              <Form.Item name="packSize" label="Units/pack" tooltip="Stock units in one pack — m per bar/reel, kg per bag, pcs per box">
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="6" />
              </Form.Item>
              <Form.Item name="perUnitFactor" label="Use/unit" tooltip="Optional — consumption factor for the later manual-issue suggestion (kg/m, g/m, pcs/frame)">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-x-3">
            <Form.Item className="col-span-6" name="shelfLifeMonths" label="Shelf life (months)" tooltip="Sealants/desiccant are moisture-sensitive; stored for future expiry alerts">
              <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item name="remarks" label="Remarks" className="dgu-remarks">
            <Input.TextArea rows={2} maxLength={2000} placeholder="Optional notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
